// src/data/phraseSchedule.js
// Wochenplan für die Übung „Redewendungen“: In den ersten Wochen kommen
// Grundlagen-Themen (Inhalte aus <lang>_topics.csv), danach die Redewendungen
// aus <lang>_phrases.csv der Reihe nach.
//
// Neues Thema: Einträge mit neuem topic-Schlüssel in <lang>_topics.csv anlegen
// und hier eine Woche ergänzen (bei beiden Tempo-Listen). Alle späteren
// Phrasen-Wochen rutschen dann automatisch eine Woche nach hinten.

// Jede Themen-Woche besteht aus Blöcken. Ein Block fasst einen oder mehrere
// topic-Schlüssel aus <lang>_topics.csv zusammen (key: eindeutiger Name, wird als
// bestandener Test gespeichert). Jeder Block endet mit einem großen Test, am
// Ende kommt ein Abschluss-Test über alle Blöcke (finalTitle).
//
// Schwer lernt die Themen in 4 Wochen, mittel und leicht im halben Tempo:
// Zahlen und Monate verteilen sich auf je zwei Wochen.
//
// Je Lernsprache gleich viele Wochen (die Redewendungen starten überall gleich), die Inhalte
// kommen aus <lang>_topics.csv. Französisch hat 70–99 als eigenen Block (soixante-dix,
// quatre-vingts …), die übrigen Zusammensetzungen heißen dort „21–69“.

const block = (key, title, topics = [key]) => ({ key, title, topics });

const WEEKDAYS_WEEK = {
  title: "Zahlen bis 100 & Wochentage",
  description: "Zehnerzahlen bis hundert und die sieben Wochentage.",
  blocks: [
    block("numbers_tens", "Zehner"),
    block("numbers_compound", "21–99"),
    block("weekdays", "Wochentage"),
  ],
  finalTitle: "Alles aus dieser Woche",
};

const TIME_WEEK = {
  title: "Uhrzeit",
  description: "Frag nach der Uhrzeit und sag, wie spät es ist.",
  blocks: [
    block("time_hours", "Volle Stunden"),
    block("time_minutes", "Viertel & halb"),
    block("time_day", "Tageszeiten"),
  ],
  finalTitle: "Alles zur Uhrzeit",
};

const TOPIC_WEEKS_HARD = [
  {
    title: "Zahlen 0–20",
    description: "Lerne die Zahlen von null bis zwanzig.",
    blocks: [
      block("numbers_0_5", "0–5"),
      block("numbers_6_10", "6–10"),
      block("numbers_11_20", "11–20", ["numbers_11_15", "numbers_16_20"]),
    ],
    finalTitle: "0–20",
  },
  WEEKDAYS_WEEK,
  {
    title: "Monate & Datum",
    description: "Die zwölf Monate und wie man ein Datum sagt.",
    blocks: [
      block("months_1_6", "Januar–Juni", ["months_1_3", "months_4_6"]),
      block("months_7_12", "Juli–Dezember", ["months_7_9", "months_10_12"]),
      block("dates", "Datum"),
    ],
    finalTitle: "Alle Monate & Datum",
  },
  TIME_WEEK,
];

const TOPIC_WEEKS_HALF_PACE = [
  {
    title: "Zahlen 0–10",
    description: "Lerne die Zahlen von null bis zehn.",
    blocks: [block("numbers_0_5", "0–5"), block("numbers_6_10", "6–10")],
    finalTitle: "0–10",
  },
  {
    title: "Zahlen 11–20",
    description: "Lerne die Zahlen von elf bis zwanzig.",
    blocks: [block("numbers_11_15", "11–15"), block("numbers_16_20", "16–20")],
    finalTitle: "11–20",
  },
  WEEKDAYS_WEEK,
  {
    title: "Monate Januar–Juni",
    description: "Die erste Hälfte der Monate.",
    blocks: [block("months_1_3", "Januar–März"), block("months_4_6", "April–Juni")],
    finalTitle: "Januar–Juni",
  },
  {
    title: "Monate Juli–Dezember & Datum",
    description: "Die zweite Hälfte der Monate und wie man ein Datum sagt.",
    blocks: [
      block("months_7_9", "Juli–September"),
      block("months_10_12", "Oktober–Dezember"),
      block("dates", "Datum"),
    ],
    finalTitle: "Juli–Dezember & Datum",
  },
  TIME_WEEK,
];

const WEEKDAYS_WEEK_FR = {
  ...WEEKDAYS_WEEK,
  blocks: [
    block("numbers_tens", "Zehner"),
    block("numbers_compound", "21–69"),
    block("numbers_70_99", "70–99"),
    block("weekdays", "Wochentage"),
  ],
};
const forFrench = (weeks) => weeks.map((w) => (w === WEEKDAYS_WEEK ? WEEKDAYS_WEEK_FR : w));
const TOPIC_WEEKS = {
  es: { hard: TOPIC_WEEKS_HARD, half: TOPIC_WEEKS_HALF_PACE },
  fr: { hard: forFrench(TOPIC_WEEKS_HARD), half: forFrench(TOPIC_WEEKS_HALF_PACE) },
};

/** Themen-Wochen für ein Tempo und eine Lernsprache (ohne Angabe: Spanisch) */
export function getTopicWeeks(pace, lang) {
  const weeks = TOPIC_WEEKS[lang] || TOPIC_WEEKS.es;
  return pace === "hard" ? weeks.hard : weeks.half;
}

// wie viele Phrasen pro Woche je Tempo; gilt auch als Wochenziel (EXP) der
// Themen-Wochen, damit alle Wochen gleich viel zählen
export function getPhrasesPerWeek(pace) {
  if (pace === "hard") return 5;
  if (pace === "medium") return 4;
  if (pace === "easy") return 3;
  return 4; // fallback
}

/**
 * Was ist in Woche `weekIndex` (ab 1) dran?
 * - { mode: "topic", title, description, blocks, finalTitle }
 * - { mode: "phrases", title, description, phraseWeek } (phraseWeek ab 1)
 */
export function getPhraseWeekPlan(weekIndex, pace, lang) {
  const week = weekIndex || 1;
  const topicWeeks = getTopicWeeks(pace, lang);
  const topicWeek = topicWeeks[week - 1];
  if (topicWeek) return { mode: "topic", ...topicWeek };

  return {
    mode: "phrases",
    title: "Redewendungen",
    description: "Lerne feste Wendungen, die im Alltag ständig vorkommen.",
    phraseWeek: week - topicWeeks.length,
  };
}

/**
 * topic-Schlüssel (<lang>_topics.csv) aller Themen-Wochen bis einschließlich Woche `weekIndex`.
 * Ihre Wörter (Zahlen, Wochentage, Monate, Uhrzeit) gelten danach in Sätzen als bekannt.
 */
export function getTopicKeysUpToWeek(weekIndex, pace, lang) {
  const keys = new Set();
  getTopicWeeks(pace, lang)
    .slice(0, Math.max(0, weekIndex || 0))
    .forEach((week) => week.blocks.forEach((b) => b.topics.forEach((t) => keys.add(t))));
  return keys;
}
